import React, { useState } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    IconButton,
    InputAdornment,
    Box,
    Typography,
    CircularProgress,
    Alert
} from "@mui/material";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import CloseIcon from "@mui/icons-material/Close";
import axios from "axios";
import swal from "sweetalert";
import { useTranslation } from "react-i18next";

export const ChangePasswordModal = ({ open, onClose }) => {
    const { t } = useTranslation();
    const [oldPassword, setOldPassword] = useState("");
    const [newPassword, setNewPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [showOldPassword, setShowOldPassword] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [errorMsg, setErrorMsg] = useState("");

    const handleCloseModal = () => {
        setOldPassword("");
        setNewPassword("");
        setConfirmPassword("");
        setShowOldPassword(false);
        setShowNewPassword(false);
        setShowConfirmPassword(false);
        setErrorMsg("");
        onClose();
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setErrorMsg("");

        if (!oldPassword) {
            setErrorMsg("Vui lòng nhập mật khẩu hiện tại!");
            return;
        }

        if (!newPassword || newPassword.length < 6) {
            setErrorMsg("Mật khẩu mới phải có tối thiểu 6 ký tự!");
            return;
        }

        if (newPassword !== confirmPassword) {
            setErrorMsg("Mật khẩu xác nhận không khớp!");
            return;
        }

        try {
            setLoading(true);
            const token = localStorage.getItem("user");
            let profile = null;
            try {
                profile = JSON.parse(localStorage.getItem("profile"));
            } catch (err) {
                profile = null;
            }

            if (!profile || !profile._id) {
                setErrorMsg("Không tìm thấy thông tin tài khoản!");
                setLoading(false);
                return;
            }

            const res = await axios.post(
                `${process.env.REACT_APP_API_URL}/auth/changepassword`,
                {
                    id: profile._id,
                    oldPassword: oldPassword,
                    password: newPassword
                },
                {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                }
            );

            setLoading(false);

            if (res.data && (res.data.status === 200 || res.data.success !== false)) {
                swal(t("success") || "Thành công", "Đổi mật khẩu thành công!", "success");
                handleCloseModal();
            } else {
                setErrorMsg(res.data?.message || "Đổi mật khẩu thất bại!");
            }
        } catch (err) {
            setLoading(false);
            setErrorMsg(err.response?.data?.message || "Có lỗi xảy ra khi đổi mật khẩu!");
        }
    };

    return (
        <Dialog
            open={open}
            onClose={handleCloseModal}
            maxWidth="xs"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: "16px",
                    p: 1,
                    boxShadow: "0 20px 40px rgba(0, 0, 0, 0.15)"
                }
            }}
        >
            <DialogTitle
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    pb: 1,
                    pt: 1.5,
                    px: 2.5
                }}
            >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.2 }}>
                    <Box
                        sx={{
                            width: 36,
                            height: 36,
                            borderRadius: "50%",
                            backgroundColor: "rgba(239, 68, 68, 0.1)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center"
                        }}
                    >
                        <LockOutlinedIcon sx={{ color: "#ef4444", fontSize: 20 }} />
                    </Box>
                    <Typography variant="h6" fontWeight="bold" fontSize="18px" color="#111827">
                        {t("change_password") || "Đổi mật khẩu"}
                    </Typography>
                </Box>
                <IconButton size="small" onClick={handleCloseModal} sx={{ color: "#9ca3af" }}>
                    <CloseIcon fontSize="small" />
                </IconButton>
            </DialogTitle>

            <form onSubmit={handleSubmit}>
                <DialogContent sx={{ pt: 1.5, pb: 1, px: 2.5 }}>
                    {errorMsg && (
                        <Alert severity="error" sx={{ mb: 2, borderRadius: "8px" }}>
                            {errorMsg}
                        </Alert>
                    )}

                    <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" fontWeight={600} color="#374151" mb={0.5}>
                            Mật khẩu hiện tại
                        </Typography>
                        <TextField
                            fullWidth
                            size="small"
                            type={showOldPassword ? "text" : "password"}
                            placeholder="Nhập mật khẩu hiện tại..."
                            value={oldPassword}
                            onChange={(e) => setOldPassword(e.target.value)}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            size="small"
                                            onClick={() => setShowOldPassword(!showOldPassword)}
                                            edge="end"
                                        >
                                            {showOldPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                                sx: { borderRadius: "10px" }
                            }}
                        />
                    </Box>

                    <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" fontWeight={600} color="#374151" mb={0.5}>
                            Mật khẩu mới
                        </Typography>
                        <TextField
                            fullWidth
                            size="small"
                            type={showNewPassword ? "text" : "password"}
                            placeholder="Nhập mật khẩu mới..."
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            size="small"
                                            onClick={() => setShowNewPassword(!showNewPassword)}
                                            edge="end"
                                        >
                                            {showNewPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                                sx: { borderRadius: "10px" }
                            }}
                        />
                    </Box>

                    <Box sx={{ mb: 1 }}>
                        <Typography variant="body2" fontWeight={600} color="#374151" mb={0.5}>
                            Xác nhận mật khẩu mới
                        </Typography>
                        <TextField
                            fullWidth
                            size="small"
                            type={showConfirmPassword ? "text" : "password"}
                            placeholder="Xác nhận lại mật khẩu mới..."
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            size="small"
                                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                            edge="end"
                                        >
                                            {showConfirmPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                                sx: { borderRadius: "10px" }
                            }}
                        />
                    </Box>
                </DialogContent>

                <DialogActions sx={{ px: 2.5, pb: 2, pt: 1, gap: 1 }}>
                    <Button
                        onClick={handleCloseModal}
                        variant="outlined"
                        sx={{
                            borderRadius: "10px",
                            textTransform: "none",
                            borderColor: "#d1d5db",
                            color: "#4b5563",
                            fontWeight: 600,
                            px: 2.5
                        }}
                    >
                        {t("close") || "Hủy"}
                    </Button>
                    <Button
                        type="submit"
                        variant="contained"
                        disabled={loading}
                        sx={{
                            borderRadius: "10px",
                            textTransform: "none",
                            backgroundColor: "#2563eb",
                            color: "#ffffff",
                            fontWeight: 600,
                            px: 3,
                            "&:hover": {
                                backgroundColor: "#1d4ed8"
                            }
                        }}
                    >
                        {loading ? <CircularProgress size={20} color="inherit" /> : t("update") || "Cập nhật"}
                    </Button>
                </DialogActions>
            </form>
        </Dialog>
    );
};

export default ChangePasswordModal;
